import {
  toTwoStepError,
  twoStepForgetAllDevices,
  twoStepForgetDevice,
  twoStepListDevicesOptions,
  twoStepListDevicesQueryKey,
  twoStepRemoveMethod,
  twoStepSetChoice,
  twoStepSetPreferred,
  twoStepStatusOptions,
  twoStepStatusQueryKey,
  useAuth,
  useTwoStepSignIn,
  type TwoStepError,
  type TwoStepIntent,
  type TwoStepMethodKind,
  type TwoStepStepsProps,
} from '@idu-nightlife/ui-kit';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { auth } from '../../../shared/services/firebase';
import {
  TWO_STEP_PASS_REQUIRED,
  type AccountSecurityDialog,
  type AccountSecurityLoad,
  type AccountSecurityViewProps,
  type RememberedDevicesLoad,
} from './account-security.types';

type Change = () => Promise<AccountSecurityDialog | null | void>;

// Read per call: a pass signs in again with a custom token, and the session's copy updates later.
const freshIdToken = async () => (await auth.currentUser?.getIdToken()) ?? '';

const withFreshToken = async () => ({
  headers: { Authorization: `Bearer ${await freshIdToken()}` },
  throwOnError: true as const,
});

export const useAccountSecurity = (): AccountSecurityViewProps => {
  const queryClient = useQueryClient();
  const { firebaseUser } = useAuth();
  const statusQuery = useQuery({ ...twoStepStatusOptions(), meta: { persist: false } });
  const devicesQuery = useQuery({ ...twoStepListDevicesOptions(), meta: { persist: false } });
  const [dialog, setDialog] = useState<AccountSecurityDialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TwoStepError | null>(null);
  const pendingChange = useRef<Change | null>(null);
  const methodIntent = useRef<'add-method' | 'change-sms'>('add-method');

  const refetchStatus = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: twoStepStatusQueryKey() }),
      queryClient.invalidateQueries({ queryKey: twoStepListDevicesQueryKey() }),
    ]);

  const session = async () => ({
    token: await freshIdToken(),
    uid: firebaseUser?.uid,
    email: firebaseUser?.email ?? undefined,
  });

  const verify = useTwoStepSignIn({
    onPassed: () => {
      verify.reset();
      const change = pendingChange.current;
      pendingChange.current = null;
      setDialog(null);
      if (change) void apply(change, true);
    },
  });

  const addMethod = useTwoStepSignIn({
    onPassed: () => {
      addMethod.reset();
      setDialog(null);
      void refetchStatus();
    },
    onRefused: (refusal) => {
      if (refusal.code !== TWO_STEP_PASS_REQUIRED) return;
      addMethod.reset();
      void askToVerify(async () => {
        await openMethodSteps(methodIntent.current);
        return { name: 'add-method' };
      });
    },
  });

  const startSteps = async (
    steps: typeof verify,
    intent: TwoStepIntent,
    next: AccountSecurityDialog,
  ) => {
    setDialog(next);
    try {
      await steps.start(await session(), intent);
    } catch (failure) {
      steps.reset();
      setDialog(null);
      setError(failure instanceof Error ? { message: failure.message } : toTwoStepError(failure));
    }
  };

  const askToVerify = (change: Change) => {
    pendingChange.current = change;
    return startSteps(verify, 'verify', { name: 'verify' });
  };

  const openMethodSteps = (intent: 'add-method' | 'change-sms') => {
    methodIntent.current = intent;
    return startSteps(addMethod, intent, { name: 'add-method' });
  };

  const apply = async (change: Change, retried = false) => {
    setBusy(true);
    setError(null);
    try {
      setDialog((await change()) ?? null);
    } catch (failure) {
      const refusal = toTwoStepError(failure);
      if (refusal.code === TWO_STEP_PASS_REQUIRED && !retried) {
        await askToVerify(change);
      } else {
        setDialog(null);
        setError(refusal);
      }
    } finally {
      setBusy(false);
      void refetchStatus();
    }
  };

  const closeDialog = () => {
    verify.reset();
    addMethod.reset();
    pendingChange.current = null;
    setDialog(null);
  };

  const changes = {
    remove:
      (kind: TwoStepMethodKind): Change =>
      async () => {
        await twoStepRemoveMethod({ ...(await withFreshToken()), path: { kind } });
      },
    turnOff: (): Change => async () => {
      await twoStepSetChoice({ ...(await withFreshToken()), body: { twoStep: 'off' } });
    },
    forgetDevice:
      (id: string): Change =>
      async () => {
        await twoStepForgetDevice({ ...(await withFreshToken()), path: { id } });
      },
    forgetAllDevices: (): Change => async () => {
      await twoStepForgetAllDevices(await withFreshToken());
    },
  };

  const onConfirm = () => {
    if (dialog?.name === 'remove') return void apply(changes.remove(dialog.kind));
    if (dialog?.name === 'turn-off') return void apply(changes.turnOff());
    if (dialog?.name === 'forget-devices') return void apply(changes.forgetAllDevices());
    setDialog(null);
  };

  const steps =
    dialog?.name === 'verify' ? verify : dialog?.name === 'add-method' ? addMethod : null;
  const twoStepSteps: TwoStepStepsProps | null = steps?.step
    ? { ...steps, step: steps.step, onCancel: closeDialog }
    : null;

  const load: AccountSecurityLoad = statusQuery.data
    ? { state: 'ready', status: statusQuery.data.data }
    : statusQuery.isError
      ? { state: 'error' }
      : { state: 'loading' };

  const devices: RememberedDevicesLoad = devicesQuery.data
    ? { state: 'ready', devices: devicesQuery.data.data }
    : devicesQuery.isError
      ? { state: 'error' }
      : { state: 'loading' };

  return {
    load,
    devices,
    busy,
    error,
    dialog,
    twoStepSteps,
    onRetryLoad: () => void statusQuery.refetch(),
    onRetryDevices: () => void devicesQuery.refetch(),
    onForgetDevice: (id) => void apply(changes.forgetDevice(id)),
    onForgetAllDevices: () => setDialog({ name: 'forget-devices' }),
    onMakePreferred: (kind) =>
      void apply(async () => {
        await twoStepSetPreferred({
          ...(await withFreshToken()),
          path: { kind },
          body: { preferred: true },
        });
      }),
    onRemove: (kind) => setDialog({ name: 'remove', kind }),
    onAddMethod: () => void openMethodSteps('add-method'),
    onChangeNumber: () => void openMethodSteps('change-sms'),
    onTurnOn: () =>
      void apply(async () => {
        await twoStepSetChoice({ ...(await withFreshToken()), body: { twoStep: 'on' } });
      }),
    onTurnOff: () => setDialog({ name: 'turn-off' }),
    onConfirm,
    onCloseDialog: closeDialog,
  };
};
